// Featured caption rule: keep captions ultra-short and scannable.
// Allowed: up to 3 hashtags (#word) OR up to 3 plain words. No mixing.
// Each token max 20 chars. Total caption max 60 chars.
// Empty captions are allowed (rendered as no caption).

export type CaptionMode = "empty" | "words" | "hashtags";

export interface CaptionValidation {
  ok: boolean;
  mode: CaptionMode;
  tokens: string[];
  reason?:
    | "too_long"
    | "too_many_tokens"
    | "token_too_long"
    | "mixed_format"
    | "bad_chars";
}

const TOKEN_MAX = 20;
const TOKENS_MAX = 3;
const CAPTION_MAX = 60;
const HASHTAG_RE = /^#[A-Za-z0-9_]{1,19}$/;
const WORD_RE = /^[A-Za-z0-9'’\-]{1,20}$/;

export function validateFeaturedCaption(input: string): CaptionValidation {
  const trimmed = (input ?? "").trim();
  if (!trimmed) return { ok: true, mode: "empty", tokens: [] };
  if (trimmed.length > CAPTION_MAX)
    return { ok: false, mode: "empty", tokens: [], reason: "too_long" };
  const tokens = trimmed.split(/\s+/);
  if (tokens.length > TOKENS_MAX)
    return { ok: false, mode: "empty", tokens, reason: "too_many_tokens" };
  if (tokens.some((t) => t.length > TOKEN_MAX))
    return { ok: false, mode: "empty", tokens, reason: "token_too_long" };
  const allTags = tokens.every((t) => t.startsWith("#"));
  const noTags = tokens.every((t) => !t.startsWith("#"));
  if (!allTags && !noTags)
    return { ok: false, mode: "empty", tokens, reason: "mixed_format" };
  const re = allTags ? HASHTAG_RE : WORD_RE;
  if (!tokens.every((t) => re.test(t)))
    return { ok: false, mode: "empty", tokens, reason: "bad_chars" };
  return { ok: true, mode: allTags ? "hashtags" : "words", tokens };
}

export function captionErrorMessage(reason: CaptionValidation["reason"]): string {
  switch (reason) {
    case "too_long": return "Caption is too long (max 60 chars).";
    case "too_many_tokens": return "Max 3 words or 3 hashtags.";
    case "token_too_long": return "Each word/tag must be ≤ 20 characters.";
    case "mixed_format": return "Use only words OR only #hashtags — not both.";
    case "bad_chars": return "Only letters, numbers, and basic punctuation allowed.";
    default: return "Invalid caption.";
  }
}

const IG_RE = /^[A-Za-z0-9._]{1,30}$/;

export function normalizeInstagramHandle(raw: string): string | null {
  if (!raw) return null;
  let h = raw.trim();
  if (!h) return null;
  // Accept full URLs (instagram.com/handle, with or without https://)
  const urlMatch = h.match(/instagram\.com\/([A-Za-z0-9._]+)/i);
  if (urlMatch) h = urlMatch[1];
  h = h.replace(/^@+/, "").replace(/\/+$/, "");
  if (!IG_RE.test(h)) return null;
  return h.toLowerCase();
}
